import React from 'react';
import { CheckCircle2, Play, Upload, Clock } from 'lucide-react';
import { Button } from '../ui/button';
import { activityMeta, dueState, DUE_STYLES, formatDue } from '../../lib/activityTypes';

const hasLaunchTarget = (a) => a.activity_type === 'assignment' || a.content_id || a.ai_course_id || a.external_url || a.document_url || a.document_id || a.document_filename;

function PoeStatus({ submission }) {
  const graded = submission.status === 'graded';
  return (
    <span className="flex flex-wrap items-center gap-2 text-xs">
      <span className={`px-2 py-1 rounded-full ${graded ? 'bg-teal-500/15 text-teal-300' : 'bg-amber-500/15 text-amber-300'}`}>
        {graded ? `Graded · ${submission.grade_percentage}%` : 'Submitted · awaiting grade'}
      </span>
      {submission.feedback && <span className="text-zinc-400">Feedback: {submission.feedback}</span>}
    </span>
  );
}

export default function LearnerActivityRow({ activity, isComplete, isNext, isEnrolled, poeSubmission, uploading, onLaunch, onPoeUpload }) {
  const meta = activityMeta(activity);
  const Icon = meta.icon;
  const isAssignment = activity.activity_type === 'assignment';
  const due = isAssignment ? dueState(activity.due_at) : null;
  return (
    <div
      className={`group relative flex gap-4 p-4 rounded-xl border transition-colors duration-200 ${isComplete ? 'bg-teal-500/[0.04] border-teal-500/25' : isNext ? 'bg-zinc-800/60 border-zinc-600' : 'bg-zinc-900/40 border-zinc-800 hover:border-zinc-700'}`}
      data-testid={`learner-activity-${activity.activity_id}`}
    >
      <div className="h-11 w-11 shrink-0 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${meta.color}1f`, color: meta.color }}>
        {isComplete ? <CheckCircle2 className="h-5 w-5 text-teal-400" /> : <Icon className="h-5 w-5" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-[0.12em]" style={{ color: meta.color }} data-testid={`learner-activity-type-${activity.activity_id}`}>{meta.label}</span>
          {activity.duration_minutes ? <span className="flex items-center gap-1 text-[11px] text-zinc-500"><Clock className="h-3 w-3" />{activity.duration_minutes} min</span> : null}
          {isComplete && <span className="text-[11px] px-2 py-0.5 rounded-full bg-teal-500/15 text-teal-300">Completed</span>}
          {due && (
            <span className={`text-[11px] px-2 py-0.5 rounded-full border ${DUE_STYLES[due]}`} data-testid={`learner-activity-due-${activity.activity_id}`}>
              {due === 'overdue' ? 'Closed' : 'Due'} {formatDue(activity.due_at)}
            </span>
          )}
          {activity.has_quiz && <span className="text-[11px] px-2 py-0.5 rounded-full bg-violet-500/15 text-violet-300" data-testid={`learner-activity-quiz-${activity.activity_id}`}>Includes quiz</span>}
          {!isComplete && isNext && <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-400/15 text-amber-300" data-testid={`learner-activity-next-${activity.activity_id}`}>Up next</span>}
        </div>
        <h4 className="text-white font-medium mt-1 leading-snug">{activity.title}</h4>
        {activity.description && <p className="text-sm text-zinc-400 mt-1 line-clamp-2">{activity.description}</p>}
        <div className="flex flex-wrap items-center gap-2 mt-3">
          {hasLaunchTarget(activity) && (
            <Button size="sm" onClick={() => onLaunch(activity)} className={isComplete ? 'bg-zinc-800 hover:bg-zinc-700 text-zinc-200' : 'bg-teal-600 hover:bg-teal-500 text-white'} data-testid={`learner-activity-launch-${activity.activity_id}`}>
              <Play className="h-3.5 w-3.5 mr-1.5" />{isAssignment ? (poeSubmission ? 'View submission' : meta.action) : isComplete ? 'Review' : meta.action}
            </Button>
          )}
          {isEnrolled && isAssignment && poeSubmission && <PoeStatus submission={poeSubmission} />}
          {isEnrolled && !isAssignment && activity.allows_poe_submission && (poeSubmission ? <PoeStatus submission={poeSubmission} /> : (
            <>
              <input type="file" accept=".pdf,.ppt,.pptx" className="hidden" id={`poe-${activity.activity_id}`} onChange={(e) => onPoeUpload(activity.activity_id, e.target.files[0])} />
              <Button size="sm" disabled={uploading} onClick={() => document.getElementById(`poe-${activity.activity_id}`).click()} className="bg-amber-500 hover:bg-amber-400 text-black" data-testid={`learner-activity-poe-${activity.activity_id}`}>
                <Upload className="h-3.5 w-3.5 mr-1.5" />{uploading ? 'Uploading…' : 'Submit evidence'}
              </Button>
              <span className="text-xs text-zinc-500">PDF or PowerPoint</span>
            </>
          ))}
        </div>
      </div>
    </div>
  );
}
